import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';

type AuthView = 'login' | 'signup';

interface AuthModalProps {
  initialView?: AuthView;
  onClose: () => void;
}

const MIN_PASSWORD_LENGTH = 8;

export default function AuthModal({ initialView = 'login', onClose }: AuthModalProps) {
  const { loginWithEmail, signupWithEmail, loginWithWallet, isLoading } = useAuth();
  const [view, setView] = useState<AuthView>(initialView);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setError('');
  };

  const switchView = (next: AuthView) => {
    resetForm();
    setView(next);
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (view === 'signup') {
      if (password.length < MIN_PASSWORD_LENGTH) {
        setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
        return;
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }
      try {
        await signupWithEmail(email, password);
        onClose();
      } catch (err: any) {
        setError(err.message || 'Signup failed. Please try again.');
      }
    } else {
      try {
        await loginWithEmail(email, password);
        onClose();
      } catch (err: any) {
        setError(err.message || 'Invalid email or password');
      }
    }
  };

  const handleMetaMask = async () => {
    setError('');
    try {
      await loginWithWallet();
      onClose();
    } catch (err: any) {
      setError(err.message || 'MetaMask authentication failed');
    }
  };

  const isLogin = view === 'login';
  const title = isLogin ? 'Sign In' : 'Create Account';

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label={title}>
      <div className="modal-box">
        <button className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        <h2 className="modal-title">{title}</h2>

        {error && <div className="modal-error">{error}</div>}

        <form onSubmit={handleEmailSubmit} className="auth-form">
          <label className="auth-label">
            Email
            <input
              type="email"
              className="auth-input"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </label>
          <label className="auth-label">
            Password
            <input
              type="password"
              className="auth-input"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              autoComplete={isLogin ? 'current-password' : 'new-password'}
              minLength={MIN_PASSWORD_LENGTH}
            />
          </label>
          {!isLogin && (
            <label className="auth-label">
              Confirm Password
              <input
                type="password"
                className="auth-input"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                minLength={MIN_PASSWORD_LENGTH}
              />
            </label>
          )}
          <button type="submit" className="btn-primary" disabled={isLoading}>
            {isLoading
              ? (isLogin ? 'Signing in…' : 'Creating account…')
              : title}
          </button>
        </form>

        <div className="auth-divider"><span>or</span></div>

        <button className="btn-metamask" onClick={handleMetaMask} disabled={isLoading}>
          <span className="metamask-icon">🦊</span>
          {isLoading ? 'Connecting…' : `${isLogin ? 'Sign in' : 'Sign up'} with MetaMask`}
        </button>

        <p className="auth-switch">
          {isLogin ? (
            <>Don't have an account?{' '}
              <button className="auth-link" onClick={() => switchView('signup')}>
                Sign up
              </button>
            </>
          ) : (
            <>Already have an account?{' '}
              <button className="auth-link" onClick={() => switchView('login')}>
                Sign in
              </button>
            </>
          )}
        </p>
      </div>
    </div>
  );
}
